import { Button, Flex, Form as FormAntd, FormInstance, Skeleton } from 'antd'
import { ReactNode, useEffect } from 'react'
import { Toolbox } from '~/plugins/toolbox/shared'
import { useValuesFlat } from '../hooks/useValueFlat'

type Props<DataType> = {
  children: ReactNode
  defaultValues?: DataType
  values?: DataType
  buttonLabel?: ReactNode
  isFetching?: boolean
  isLoading?: boolean
  isFlatten?: boolean
  widthMax?: string
  onSubmit(values: DataType): void
  onChange?(
    valuesChanged: Partial<DataType>,
    values: DataType,
    isComplete: boolean,
    form?: FormInstance,
  ): void
  renderButton?: (props: { loading: boolean; htmlType: 'submit' }) => ReactNode
}

export const Form = <DataType extends Record<string, any>>({
  values,
  defaultValues,
  buttonLabel = 'Save',
  isFetching = false,
  isLoading = false,
  isFlatten = true,
  children,
  widthMax = '800px',
  onSubmit,
  onChange,
  renderButton,
}: Props<DataType>) => {
  const [form] = FormAntd.useForm<DataType>()

  const flatter = useValuesFlat()

  const handleSubmit = async (valuesFlat: DataType) => {
    if (isFlatten) {
      const values = flatter.toObject(valuesFlat) as DataType
      onSubmit(values)
    } else {
      onSubmit(valuesFlat)
    }
  }

  const handleChange = async (
    valuesChangedFlat: Partial<DataType>,
    valuesFlat: DataType,
  ) => {
    let valuesChanged: any = valuesChangedFlat
    let values: any = valuesFlat

    if (isFlatten) {
      valuesChanged = flatter.toObject(valuesChangedFlat) as Partial<DataType>
      values = flatter.toObject(valuesFlat) as DataType
    }

    await Toolbox.Utility.sleep(0)

    const isComplete = form
      .getFieldsError()
      .some(({ errors }) => errors.length === 0)

    onChange?.(valuesChanged, values, isComplete, form)
  }

  useEffect(() => {
    if (isFlatten) {
      form.setFieldsValue(flatter.toFlat(values))
    } else {
      form.setFieldsValue(values)
    }
  }, [values])

  return (
    <>
      <FormAntd
        initialValues={defaultValues}
        form={form}
        onFinish={handleSubmit}
        onValuesChange={handleChange}
        layout="vertical"
        requiredMark={false}
      >
        <Flex vertical justify="center" align="center" className="w-full">
          <Flex vertical gap={8} style={{ maxWidth: widthMax, width: '100%' }}>
            {isFetching && <Skeleton paragraph={{ rows: 5 }} />}

            {!isFetching && (
              <>
                {children}
                {renderButton &&
                  renderButton({ loading: isLoading, htmlType: 'submit' })}
                {!renderButton && (
                  <Flex
                    className="w-full"
                    justify="center"
                    style={{ position: 'sticky', bottom: 0 }}
                  >
                    <Button
                      className="w-full"
                      htmlType="submit"
                      type="primary"
                      loading={isLoading}
                    >
                      {buttonLabel}
                    </Button>
                  </Flex>
                )}
              </>
            )}
          </Flex>
        </Flex>
      </FormAntd>
    </>
  )
}
